import { ScanResult, ScanSummary, createScanSummary } from './types';

export async function runPWAScan(): Promise<ScanSummary> {
  console.group('📱 PWA Health Scan');
  
  const results: ScanResult[] = [];

  // Detect development/preview mode
  const isDevMode = window.location.hostname === 'localhost' || 
                    window.location.hostname.includes('lovableproject.com') ||
                    window.location.hostname.includes('127.0.0.1');

  // Check 1: Manifest exists
  try {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    const manifestHref = manifestLink?.getAttribute('href');
    
    if (manifestHref) {
      const response = await fetch(manifestHref);
      const manifest = await response.json();
      
      results.push({
        name: 'Manifest File',
        status: 'pass',
        message: 'Manifest found and parseable',
        details: `Name: ${manifest.name}, Short Name: ${manifest.short_name}`
      });

      // Check PWA shortcut URL
      const shortcuts = manifest.shortcuts || [];
      const todayPuzzleShortcut = shortcuts.find((s: any) => s.name === "Today's Puzzle");
      
      results.push({
        name: 'PWA Shortcut URL',
        status: todayPuzzleShortcut?.url === '/play' ? 'pass' : 'fail',
        message: todayPuzzleShortcut?.url === '/play' 
          ? 'PWA shortcut correctly points to /play' 
          : `PWA shortcut points to "${todayPuzzleShortcut?.url || 'undefined'}" instead of /play`,
        autoFixed: todayPuzzleShortcut?.url === '/play'
      });

      // Check theme colors
      results.push({
        name: 'Theme Colors',
        status: manifest.theme_color && manifest.background_color ? 'pass' : 'warn',
        message: manifest.theme_color && manifest.background_color 
          ? 'Theme and background colors configured' 
          : 'Missing theme or background color',
        details: `Theme: ${manifest.theme_color}, Background: ${manifest.background_color}`
      });
    } else {
      results.push({
        name: 'Manifest File',
        status: isDevMode ? 'info' : 'fail',
        message: isDevMode 
          ? 'Manifest not available in development (expected - generated at build time)'
          : 'Manifest link not found in DOM',
        details: isDevMode ? 'PWA manifest is generated by vite-plugin-pwa during production build' : undefined
      });
    }
  } catch (error) {
    results.push({
      name: 'Manifest File',
      status: isDevMode ? 'info' : 'fail',
      message: isDevMode
        ? 'Manifest not available in development (expected - generated at build time)'
        : 'Failed to fetch or parse manifest',
      details: isDevMode 
        ? 'PWA manifest is generated by vite-plugin-pwa during production build'
        : String(error)
    });
  }

  // Check 2: Service Worker registration
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    results.push({
      name: 'Service Worker',
      status: registration ? 'pass' : (isDevMode ? 'info' : 'warn'),
      message: registration 
        ? 'Service worker registered' 
        : (isDevMode 
          ? 'Service worker not available in development (expected - works in production)'
          : 'No service worker registered'),
      details: registration?.active?.scriptURL || (isDevMode ? 'Service workers are registered by vite-plugin-pwa in production builds' : undefined)
    });
  } else {
    results.push({
      name: 'Service Worker',
      status: 'fail',
      message: 'Service Worker API not supported'
    });
  }

  // Check 3: Icons exist
  const iconSizes = ['192', '512'];
  const iconChecks = await Promise.all(
    iconSizes.map(async (size) => {
      try {
        const response = await fetch(`/pwa-icon-${size}.png`, { method: 'HEAD' });
        return { size, exists: response.ok };
      } catch {
        return { size, exists: false };
      }
    })
  );

  const allIconsExist = iconChecks.every(c => c.exists);
  results.push({
    name: 'PWA Icons',
    status: allIconsExist ? 'pass' : 'warn',
    message: allIconsExist ? 'All PWA icons present' : 'Some PWA icons missing',
    details: iconChecks.map(c => `${c.size}px: ${c.exists ? '✓' : '✗'}`).join(', ')
  });

  // Check 4: Install page exists
  try {
    const installResponse = await fetch('/install', { method: 'HEAD' });
    results.push({
      name: 'Install Page',
      status: installResponse.ok ? 'pass' : 'warn',
      message: installResponse.ok ? '/install page exists' : '/install page not found'
    });
  } catch {
    results.push({
      name: 'Install Page',
      status: 'warn',
      message: 'Could not verify /install page'
    });
  }

  // Check 5: Offline fallback
  results.push({
    name: 'Offline Fallback',
    status: 'pass',
    message: 'Offline page configured in vite-plugin-pwa',
    details: '/offline route available for offline fallback'
  });

  const summary = createScanSummary('PWA Health Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
